import { Model, ApiUsageResult } from '../types/usage';

/**
 * Gets a token indicating if a system API key is available for a given provider.
 * 
 * SECURITY: This function no longer directly returns API keys - instead, it
 * returns a boolean or token indicating availability. API keys should be accessed
 * only on the server-side through proxies.
 * 
 * @param provider - The provider name (e.g., 'openai', 'anthropic')
 * @returns A token indicating the system API is available, or null if not configured
 */
function getSystemApiKey(provider: string): string | null {
  // Check if the environment variable exists without exposing its value
  // Remove VITE_ prefix for Cloudflare deployment to prevent client-side exposure
  const envVar = `SYSTEM_${provider.toUpperCase()}_API_KEY`;
  
  // For Anthropic which already uses a proxy
  if (provider.toLowerCase() === 'anthropic') {
    return import.meta.env[envVar] ? 'PROXY_TOKEN' : null;
  }
  
  // For other providers, we should check if the key exists without returning it
  const hasKey = Object.keys(import.meta.env).includes(envVar);
  
  // Return a token indicating the key exists, instead of the actual key
  // IMPORTANT: In production, this should be replaced with a server-side proxy check
  return hasKey ? 'USE_SERVER_PROXY' : null;
}

/**
 * Determines which API to use (user's API or system's API) based on top-up value and user API key availability.
 *
 * @param {number} topUpValue - The user's current top-up value (balance).
 * @param {boolean} hasUserApiKey - Indicates whether the user has configured their own API key for the model.
 * @param {Model} model - An object representing the model to be used, including its name, provider, and system API cost.
 * @returns {ApiUsageResult} - An object indicating the action to take, an updated top-up value (if applicable), and a message for the user.
 */
export function determineApiUsage(
  topUpValue: number,
  hasUserApiKey: boolean,
  model: Model
): ApiUsageResult {
  // If user has their own API key, always use it first
  if (hasUserApiKey) {
    return {
      action: 'useUserApi',
      message: `Using your personal API for the ${model.name} model from ${model.provider}.`,
    };
  }

  // Check if we have a system API key for this provider
  const systemApiKey = getSystemApiKey(model.provider);

  // If no system API key is configured, user must provide their own
  if (!systemApiKey) {
    return {
      action: 'showErrorMessage',
      message: `No system API available for ${model.provider}. Please configure your own API key to use this model.`,
    };
  }

  // If we have a system API key, check if user has sufficient balance
  if (topUpValue <= 0 || topUpValue < model.systemApiCost) {
    return {
      action: 'showErrorMessage',
      updatedTopUpValue: Math.max(0, topUpValue),
      message: topUpValue <= 0 
        ? `Your balance is $0.0000. To continue using the ${model.name} model from ${model.provider}, please add funds or configure your own API key.`
        : `Your balance ($${topUpValue.toFixed(4)}) is insufficient for this operation (cost: $${model.systemApiCost.toFixed(4)}). Please add more funds or configure your own API key.`,
    };
  }

  // If we get here, we have a system API key and user has sufficient balance
  const updatedTopUpValue = topUpValue - model.systemApiCost;
  return {
    action: 'useSystemApi',
    updatedTopUpValue: updatedTopUpValue,
    systemApiKey, // Include the system API key to be used
    message: `Using the system's API for the ${model.name} model from ${model.provider}. Cost: $${model.systemApiCost.toFixed(4)}. New balance: $${updatedTopUpValue.toFixed(4)}.`,
  };
}